/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * AUTO COLOR MEDIA FILTERING
 *
 * Automatically filters product media based on variant color selection.
 * Works together with the Liquid filtering in product-media.liquid.
 *
 * Uses position-based ranges: each color's images are consecutive,
 * starting at the featured_media position for that color.
 */
(function() {
  const COLOR_LABELS = ['color', 'colour', 'farve', 'couleur', 'colore', 'cor', 'kleur', 'szín', 'barva'];
  let latestRequestId = 0;
  let pendingTimer = null;

  // Theme media logic listens to variant:change and can reset filtering.
  // We listen to both events and run slightly delayed to always apply color filtering last.
  document.addEventListener('variant:change', scheduleColorFiltering);
  document.addEventListener('variant:changed', scheduleColorFiltering);

  function scheduleColorFiltering(event) {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function() {
      handleColorFiltering(event);
    }, 70);
  }

  async function handleColorFiltering(event) {
    try {
      const variant = event && event.detail ? event.detail.variant : null;
      const rawFormRef = event && event.detail ? event.detail.formId : null;
      const eventTarget = event && event.target ? event.target : null;
      let formId = null;

      // In this theme build, "formId" can be a DOM node (often input[name="id"])
      // rather than a string id.
      if (typeof rawFormRef === 'string') {
        formId = rawFormRef;
      } else if (rawFormRef && typeof rawFormRef.getAttribute === 'function') {
        formId = rawFormRef.getAttribute('id');

        if (!formId && rawFormRef.form && typeof rawFormRef.form.getAttribute === 'function') {
          formId = rawFormRef.form.getAttribute('id');
        }
      }

      if (!formId && eventTarget && typeof eventTarget.getAttribute === 'function') {
        formId = eventTarget.getAttribute('id');
      }

      if (!variant || !Array.isArray(variant.options)) return;

      const productMedia = document.querySelector('product-media');
      if (!productMedia) return;

      // Ignore events coming from other product forms (quick-buy, sticky forms, etc.)
      if (formId && productMedia.getAttribute('form-id') !== formId) return;

      const productHandle = productMedia.getAttribute('product-handle');
      if (!productHandle) return;

      const requestId = ++latestRequestId;

      // Load product data
      const product = await ProductLoader.load(productHandle);
      if (requestId !== latestRequestId) return;
      if (!product || !Array.isArray(product.media) || !Array.isArray(product.variants) || !Array.isArray(product.options)) return;

      // Check if any media uses alt-text filtering (let theme handle it instead)
      const hasAltTagFiltering = product.media.some(function(media) {
        return typeof media.alt === 'string' && media.alt.indexOf('#') !== -1;
      });
      if (hasAltTagFiltering) return;

      // Find color option index
      let colorOptionIndex = -1;
      for (let i = 0; i < product.options.length; i++) {
        const optionEntry = product.options[i];
        const optionName = typeof optionEntry === 'object' ? optionEntry.name : optionEntry;

        if (typeof optionName === 'string' && COLOR_LABELS.includes(optionName.toLowerCase())) {
          colorOptionIndex = i;
          break;
        }
      }

      if (colorOptionIndex < 0) return;

      // Get selected color value from variant
      const selectedColor = variant.options[colorOptionIndex];
      if (typeof selectedColor !== 'string' || selectedColor.length === 0) return;

      // Collect start positions for each unique color (lowest position wins)
      const colorStartPositions = new Map();

      for (const productVariant of product.variants) {
        if (!productVariant || !productVariant.featured_media || typeof productVariant.featured_media.position !== 'number') continue;

        const variantOptions = Array.isArray(productVariant.options) ? productVariant.options : [];
        const variantColor = variantOptions[colorOptionIndex];

        if (typeof variantColor !== 'string' || variantColor.length === 0) continue;

        const currentStart = colorStartPositions.get(variantColor);
        const nextStart = productVariant.featured_media.position;

        if (typeof currentStart !== 'number' || nextStart < currentStart) {
          colorStartPositions.set(variantColor, nextStart);
        }
      }

      const selectedColorStart = colorStartPositions.get(selectedColor);
      if (typeof selectedColorStart !== 'number') return;

      // Sort all start positions to find ranges
      const allStarts = Array.from(colorStartPositions.values()).filter(function(pos) {
        return typeof pos === 'number';
      }).sort(function(a, b) {
        return a - b;
      });

      // Find end position (next color's start or total media + 1)
      let selectedColorEnd = product.media.length + 1;
      for (const position of allStarts) {
        if (position > selectedColorStart) {
          selectedColorEnd = position;
          break;
        }
      }

      // Build list of media IDs to filter out (outside position range)
      const filteredMediaIds = product.media.filter(function(media) {
        return media.position < selectedColorStart || media.position >= selectedColorEnd;
      }).map(function(media) {
        return media.id;
      });
      const filteredMediaIdSet = new Set(filteredMediaIds);

      const visibleMediaIds = product.media.filter(function(media) {
        return !filteredMediaIdSet.has(media.id);
      }).map(function(media) {
        return media.id;
      });
      if (visibleMediaIds.length === 0) return;

      // Get carousel
      const carousel = productMedia.querySelector('flickity-carousel');
      if (!carousel) return;

      const flickity = await carousel.flickityInstance;
      if (requestId !== latestRequestId) return;
      if (!flickity) return;

      // Determine which media to select after reload
      let selectedMediaId = variant.featured_media && typeof variant.featured_media.id === 'number'
        ? variant.featured_media.id
        : null;

      if (selectedMediaId === null || filteredMediaIdSet.has(selectedMediaId)) {
        selectedMediaId = visibleMediaIds[0];
      }

      // Destroy carousel before changing classes
      flickity.destroy();

      // Toggle .is-filtered class on all media items (main carousel, thumbnails, dots)
      productMedia.querySelectorAll('[data-media-id]').forEach(function(item) {
        const mediaId = parseInt(item.getAttribute('data-media-id'), 10);

        if (Number.isNaN(mediaId)) return;

        const isFiltered = filteredMediaIdSet.has(mediaId);
        const isSelected = mediaId === selectedMediaId;

        item.classList.toggle('is-filtered', isFiltered);
        item.classList.toggle('is-selected', isSelected);
        item.classList.toggle('is-initial-selected', isSelected);
      });

      // Reload carousel with new filtering
      carousel.reload();
    } catch (error) {
      console.error('Color media filtering failed:', error);
    }
  }
})();
